<template>
  <PortalLayout section="Respond">
    <div class="mt-4">
      <div v-if="isPropertyLoading || isResponsesLoading"><Loading /></div>
      <div v-else>
        <div>
          <p class="mb-4">
            Here you can override respond content from the Respond Library. If you do not
            see the role or scenario you want to override, make sure it's in the global
            library.
          </p>
        </div>
        <div class="mt-8 mb-4">
          <form
            v-on:submit.prevent
            class="w-full lg:w-1/2 mb-4 lg:mb-0 lg:float-right flex"
          >
            <input
              v-model.lazy="responseSearchString"
              placeholder="Search this portal's response content"
              class="flex-grow shadow appearance-none border rounded py-1 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            <SearchButton @click="searchResponseContent" type="submit">
              Search
            </SearchButton>
          </form>
          <EditButton @click="chooseVisibility" class="m-1">
            Choose Scenario Visibility
          </EditButton>
          <EditButton @click="editSortOrder" class="m-1">
            Edit Scenario Display Order
          </EditButton>
        </div>
        <div class="lg:w-1/2 2xl:w-1/3">
          <BaseSelect
            class="mb-4"
            label="View Mode"
            v-model="viewMode"
            :options="respondViewOptions"
            :default-option="defaultViewOption"
          />
        </div>
        <BaseCheckbox
          label="Only Show Overriden Content"
          :modelValue="onlyShowOverridden"
          @update:modelValue="onlyShowOverridden = $event"
        />

        <div v-if="viewMode === RespondViewMode.GroupByScenario" class="p-4 pt-0">
          <div v-if="!responses || responses.length === 0" class="pt-4">
            -- No Overridden Responses --
          </div>
          <div v-for="scenarioName in scenarioNames">
            <div class="text-lg pb-2 pt-8">{{ scenarioName }}</div>
            <Disclosure
              v-for="response in responses.filter(
                (r) => r.responseTypeName === scenarioName
              )"
              :key="response.responseId"
            >
              <DisclosureButton
                class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-gray-900 border-t hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75"
                :class="response.responseId === null ? 'bg-gray-100' : 'bg-gray-300'"
                @click="selectClick(response)"
              >
                <div>
                  {{ response.roleName }}
                  <span
                    v-if="getSiteRoleByName(response.roleName)?.isStaffRole"
                    class="text-gray-500"
                  >
                    (Staff)
                  </span>
                </div>
                <span v-if="response.isHidden">(hidden)</span>
              </DisclosureButton>
              <div
                v-if="
                  selectedName == response.responseTypeName &&
                  selectedSiteRoleId === response.siteRoleId
                "
              >
                <DisclosurePanel static>
                  <div class="m-3">
                    <div v-if="isEditorVisible" class="pb-3">
                      <Loading v-if="isSaving" />
                      <ErrorDisplay
                        v-else
                        :is-visible="saveError.length > 0"
                        :message="saveError"
                      />
                      <div v-if="Ckeditor && Editor" class="ckeditor-text mb-3">
                        <component :is="Ckeditor" :editor="Editor" v-model="respondContent" />
                      </div>
                      <BaseCheckbox label="Is Hidden" v-model="isResponseHidden" />
                      <div class="mt-3 flex">
                        <BaseButton
                          @click="deleteResponse"
                          v-if="isEditingExistingResponse"
                          class="text-blue-900 bg-red-100"
                        >
                          Delete
                        </BaseButton>
                        <div class="flex-grow" />
                        <BaseButton @click="saveResponse" class="mr-4">Save</BaseButton>
                        <BaseButton @click="isEditorVisible = false">Cancel</BaseButton>
                      </div>
                    </div>
                    <div v-else>
                      <AddButton
                        v-if="response.responseId === null"
                        @click="overrideResponse(response)"
                      >
                        Add Override
                      </AddButton>
                      <EditButton v-else @click="overrideResponse(response)" class="h-10">
                        Edit Override
                      </EditButton>
                      <div class="border my-2 p-2 response-steps text-gray-500">
                        <span v-html="response.responseText"></span>
                      </div>
                      <AddButton
                        v-if="response.responseId === null"
                        @click="overrideResponse(response)"
                      >
                        Add Override
                      </AddButton>
                      <EditButton v-else @click="overrideResponse(response)" class="h-10">
                        Edit Override
                      </EditButton>
                    </div>
                  </div>
                </DisclosurePanel>
              </div>
            </Disclosure>
          </div>
        </div>

        <div v-else-if="viewMode === RespondViewMode.GroupByRole" class="p-4 pt-0">
          <div v-if="!responses || responses.length === 0" class="pt-4">
            -- No Overridden Responses --
          </div>
          <div v-for="roleName in roleNames">
            <div class="text-lg pb-2 pt-8">
              {{ roleName }}
              <span v-if="getSiteRoleByName(roleName)?.isStaffRole" class="text-gray-500">
                (Staff)
              </span>
            </div>
            <Disclosure
              v-for="response in responses.filter((r) => r.roleName === roleName)"
              :key="response.responseId"
            >
              <DisclosureButton
                class="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-gray-900 border-t hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75"
                :class="response.responseId === null ? 'bg-gray-100' : 'bg-gray-300'"
                @click="selectClick(response)"
              >
                <span>{{ response.responseTypeName }}</span>
                <span v-if="response.isHidden">(hidden)</span>
              </DisclosureButton>
              <div
                v-if="
                  selectedName == response.responseTypeName &&
                  selectedSiteRoleId === response.siteRoleId
                "
              >
                <DisclosurePanel static>
                  <div class="m-3">
                    <div v-show="isEditorVisible && isLayoutReady" class="pb-3">
                      <Loading v-if="isSaving" />
                      <ErrorDisplay
                        v-else
                        :is-visible="saveError.length > 0"
                        :message="saveError"
                      />
                      <div v-if="Ckeditor && Editor" class="ckeditor-text mb-3">
                        <component :is="Ckeditor" :editor="Editor" v-model="respondContent" />
                      </div>
                      <BaseCheckbox label="Is Hidden" v-model="isResponseHidden" />
                      <div class="mt-3 flex">
                        <BaseButton
                          @click="deleteResponse"
                          v-if="isEditingExistingResponse"
                          class="text-blue-900 bg-red-100"
                        >
                          Delete
                        </BaseButton>
                        <div class="flex-grow" />
                        <BaseButton @click="saveResponse" class="mr-4">Save</BaseButton>
                        <BaseButton @click="isEditorVisible = false">Cancel</BaseButton>
                      </div>
                    </div>
                    <div v-show="!isEditorVisible">
                      <AddButton
                        v-if="response.responseId === null"
                        @click="overrideResponse(response)"
                      >
                        Add Override
                      </AddButton>
                      <EditButton v-else @click="overrideResponse(response)" class="h-10">
                        Edit Override
                      </EditButton>
                      <div class="border my-2 p-2 response-steps text-gray-500">
                        <span v-html="response.responseText"></span>
                      </div>
                      <AddButton
                        v-if="response.responseId === null"
                        @click="overrideResponse(response)"
                      >
                        Add Override
                      </AddButton>
                      <EditButton v-else @click="overrideResponse(response)" class="h-10">
                        Edit Override
                      </EditButton>
                    </div>
                  </div>
                </DisclosurePanel>
              </div>
            </Disclosure>
          </div>
        </div>

        <div v-else-if="viewMode === RespondViewMode.Grid" class="p-4">
          <div v-if="!responses || responses.length === 0">
            -- No Overridden Responses --
          </div>
          <div class="mt-4 bg-blue-900 w-full text-white text-center text-4xl py-5">
            Feature Coming Soon!
          </div>
        </div>

        <div v-else-if="viewMode === RespondViewMode.Dump" class="p-4">
          <div v-if="!responses || responses.length === 0">
            -- No Overridden Responses --
          </div>
          <div class="mt-4 w-full">
            <h1 class="text-3xl">Responses for {{ property.name }}</h1>
            <div v-for="response in responses">
              <div class="my-3" v-if="!response.isHidden">
                <div class="text-lg font-bold">
                  {{ response.responseTypeName }}
                </div>
                <div class="font-bold">
                  {{ response.roleName }}
                </div>
                <div>
                  <span class="response-steps" v-html="response.responseText"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditPopup
        entity-name="Scenario Visibility"
        :is-visible="isEditVisibilityPopupVisible"
        :is-edit-mode="true"
        :save-error="saveError"
        :delete-enabled="false"
        @close="isEditVisibilityPopupVisible = false"
        @save="saveVisibility"
      >
        <BaseCheckboxGroup
          label="Enable these scenarios:"
          :options="responseTypeOptions"
          :model-value="selectedResponseTypes"
          :vertical="true"
        />
      </EditPopup>

      <EditPopup
        entity-name="Scenario Sort Order"
        :is-visible="isEditScenarioSortOrderPopupVisible"
        :is-edit-mode="true"
        :save-error="saveError"
        :delete-enabled="false"
        @close="isEditScenarioSortOrderPopupVisible = false"
        @save="saveSortOrder"
      >
        <div v-for="(responseType, i) in responseSortOrder" :key="i">
          <div class="flex">
            <i-heroicons-outline-chevron-up
              class="h-4 w-4 m-1 inline float-left align-middle"
              :class="
                responseType.sortOrder ==
                Math.min(...responseSortOrder.map((r) => r.sortOrder))
                  ? 'text-gray-400'
                  : 'cursor-pointer hover:bg-blue-200'
              "
              @click="swapResponseTypeSort(responseType.sortOrder, true)"
            />
            <i-heroicons-outline-chevron-down
              class="h-4 w-4 m-1 inline float-left align-middle"
              :class="
                responseType.sortOrder ==
                Math.max(...responseSortOrder.map((r) => r.sortOrder))
                  ? 'text-gray-400'
                  : 'cursor-pointer hover:bg-blue-200'
              "
              @click="swapResponseTypeSort(responseType.sortOrder, false)"
            />
            <div>{{ responseType.name }}</div>
          </div>
        </div>
      </EditPopup>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { useRespondStore } from "../../store/respondStore";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import {
  ResponseDTO,
  ResponseTypeDTO,
  ResponseTypeOrder,
  ResponseTypeOrderRequest,
  ResponseVisibilityRequest,
  SaveResponse,
} from "../../store/generatedFetchClient";

import router from "../../router";
import { NameValue } from "../../sharedTypes";
import { useCKEditor } from "../../composables/useCKEditor";

// Load CKEditor using composable
const { Ckeditor, Editor, isEditorReady: isLayoutReady } = useCKEditor();

enum RespondViewMode {
  GroupByScenario = "0", // baseselect is currently broken and screws up number types
  GroupByRole = "1", // use strings instead
  Grid = "2",
  Dump = "3",
}
let defaultViewOption = ref({
  name: "Group By Scenario",
  value: RespondViewMode.GroupByScenario,
} as NameValue);
let respondViewOptions = ref([
  { name: "Group By Role", value: RespondViewMode.GroupByRole },
  { name: "Grid", value: RespondViewMode.Grid },
  { name: "Dump", value: RespondViewMode.Dump },
] as NameValue[]);

const bldgStore = useBldgStore();
const respondStore = useRespondStore();

var isPropertyLoading = computed(() => bldgStore.isLoading);
let isResponsesLoading = ref(false);
var property = computed(() => bldgStore.property);
var responses = computed(() =>
  onlyShowOverridden.value
    ? respondStore.selectedResponses.filter((r) => r.responseId !== null)
    : respondStore.selectedResponses
);
var roleNames = computed(() => {
  var roles = [] as string[];
  responses.value.forEach((r) => {
    if (!roles.includes(r.roleName)) roles.push(r.roleName);
  });
  return roles;
});
var scenarioNames = computed(() => {
  var scenarios = [] as string[];
  responses.value.forEach((r) => {
    if (!scenarios.includes(r.responseTypeName)) scenarios.push(r.responseTypeName);
  });
  console.log("Respond page. Scenarios", scenarios);
  return scenarios;
});

let viewMode = ref(RespondViewMode.GroupByScenario);
let onlyShowOverridden = ref(false);

let selectedName = ref("activeshooter");
let selectedSiteRoleId = ref(0);
let selectedItem = ref<ResponseDTO | null>(null);

onMounted(async () => {
  await loadData();
});

watch(property, loadData);

async function loadData() {
  console.log("Respond page load data");
  isResponsesLoading.value = true;
  if (property.value !== null && property.value.portalId) {
    await bldgStore.tryGetBuildingData(property.value.siteCode);
    await respondStore.getResponsesByPortal(property.value.portalId, true);
    await respondStore.getResponseTypes();
    await respondStore.getExcludedResponseTypes(property.value.propertyId);

    if (property.value?.responseLibId)
      await respondStore.getLibrary(property.value.responseLibId);
  }
  isResponsesLoading.value = false;
  isLayoutReady.value = true;
}

function selectClick(response: ResponseDTO) {
  // Prevent selection when editing
  if (isEditorVisible.value) return;

  // Same item clicked twice => Toggle selection
  if (
    response.responseTypeName === selectedName.value &&
    response.siteRoleId === selectedSiteRoleId.value
  ) {
    selectedName.value = "";
    selectedItem.value = {};
    selectedSiteRoleId.value = 0;
    return;
  }

  selectedName.value = response.responseTypeName ?? "";
  selectedItem.value = response;
  selectedSiteRoleId.value = response.siteRoleId ?? 0;
}

let saveError = ref("");

// #region Edit Response
let respondContent = ref("");
let isResponseHidden = ref(false);
let isEditingExistingResponse = ref(false);
let isEditorVisible = ref(false);
let isSaving = ref(false);

let overrideResponse = function (response: ResponseDTO) {
  console.log("Override response", response);
  selectedName.value = response.responseTypeName ?? "";
  selectedItem.value = response;
  respondContent.value = response.responseText ?? "";
  isResponseHidden.value = response.isHidden ?? false;

  saveError.value = "";
  isEditingExistingResponse.value = response.responseId !== null;
  isEditorVisible.value = true;
};

let saveResponse = async function () {
  if (isSaving.value) return;
  isSaving.value = true;
  saveError.value = "";

  if (!selectedItem.value) {
    console.warn("No response item found to save");
    return;
  }

  let saveResponse = {
    responseId: selectedItem.value.responseId,
    responseLibItemId: selectedItem.value.responseLibItemId,
    siteRoleId: selectedItem.value.siteRoleId,
    responseText: respondContent.value,
    isHidden: isResponseHidden.value,
  } as SaveResponse;

  saveError.value = await respondStore.SaveResponse(
    property.value.portalId,
    saveResponse
  );
  if (!saveError.value) {
    await respondStore.getResponsesByPortal(property.value.portalId, true);
    isEditorVisible.value = false;
  }
  isSaving.value = false;
};

let deleteResponse = async function () {
  if (isSaving.value) return;
  isSaving.value = true;
  saveError.value = "";

  if (!selectedItem.value) {
    console.warn("No response item found to delete");
    return;
  }

  saveError.value = await respondStore.deleteResponse(selectedItem.value.responseId);
  if (!saveError.value) {
    await respondStore.getResponsesByPortal(property.value.portalId, true);
    isEditorVisible.value = false;
  }
  isSaving.value = false;
};
// #endregion Edit Response

// #region Edit Role/Response Type Visibility
let isEditVisibilityPopupVisible = ref(false);
let selectedResponseTypes = ref([]);
let selectedRoles = ref([]);
let responseTypeOptions = computed(() => {
  var responseTypes = [] as NameValue[];
  let library = respondStore.selectedLibrary;
  library?.responseLibItems // Only show options for scenarios used by portal's library AND roles
    .filter((i) => property.value.siteRoles.find((r) => r.roleTypeId === i.roleTypeId))
    .forEach((i) => {
      if (!responseTypes.find((t) => t.value === i.responseTypeId))
        responseTypes.push({
          name: i.responseTypeName,
          value: i.responseTypeId,
        } as NameValue);
    });
  return responseTypes;
});

let chooseVisibility = function () {
  let ex = respondStore.selectedExcludedResponseTypes.map((t) => t.responseTypeId);
  selectedResponseTypes.value = respondStore.responseTypes
    .map((t) => t.responseTypeId)
    .filter((t) => !ex.includes(t));
  selectedRoles.value = property.value.siteRoles
    .filter((r) => !r.isExcludedFromResponses)
    .map((r) => r.siteRoleId);

  saveError.value = "";
  isEditVisibilityPopupVisible.value = true;
};

let saveVisibility = async function () {
  let saveVisibilityRequest = {
    portalId: property.value.portalId,
    // User selections should be "inverted" so as to pass only unchecked options
    excludedResponseTypeIds: respondStore.responseTypes
      .map((t) => t.responseTypeId)
      .filter((t) => !selectedResponseTypes.value.includes(t)),
    excludedSiteRoleIds: property.value.siteRoles
      .map((r) => r.siteRoleId)
      .filter((r) => !selectedRoles.value.includes(r)),
  } as ResponseVisibilityRequest;

  console.log(saveVisibilityRequest);

  saveError.value = await respondStore.saveResponseVisibility(saveVisibilityRequest);
  if (!saveError.value) {
    await respondStore.getExcludedResponseTypes(property.value.propertyId);
    await bldgStore.tryGetBuildingData(bldgStore.siteCode, true);
    isEditVisibilityPopupVisible.value = false;
  }
};

// #endregion Edit Role/Scenario Visibility

// #region Edit Scenario Sort Order
let isEditScenarioSortOrderPopupVisible = ref(false);
let responseSortOrder = ref(null as ResponseTypeDTO[]);

let editSortOrder = function () {
  //Reset values and Assign fresh sortOrder values based off the current order (in case of unmigrated)
  let respondSortOrderValue = 0;
  responseSortOrder.value = property.value.responseTypes.map((responseType) => {
    return { ...responseType, sortOrder: respondSortOrderValue++ };
  });
  saveError.value = "";
  isEditScenarioSortOrderPopupVisible.value = true;
};

function swapResponseTypeSort(sortId: number, sortUp: boolean) {
  let selectedResponseTypeId = responseSortOrder.value.find(
    (responseType) => responseType.sortOrder == sortId
  ).responseTypeId;

  if (sortUp) {
    let swapResponseTypeId = responseSortOrder.value.find(
      (responseType) => responseType.sortOrder == sortId - 1
    ).responseTypeId;
    responseSortOrder.value.find(
      (responseType) => responseType.responseTypeId == selectedResponseTypeId
    ).sortOrder = sortId - 1;
    responseSortOrder.value.find(
      (responseType) => responseType.responseTypeId == swapResponseTypeId
    ).sortOrder = sortId;
  } else {
    let swapResponseTypeId = responseSortOrder.value.find(
      (responseType) => responseType.sortOrder == sortId + 1
    ).responseTypeId;
    responseSortOrder.value.find(
      (responseType) => responseType.responseTypeId == selectedResponseTypeId
    ).sortOrder = sortId + 1;
    responseSortOrder.value.find(
      (responseType) => responseType.responseTypeId == swapResponseTypeId
    ).sortOrder = sortId;
  }

  responseSortOrder.value.sort((x, y) => {
    if (x.sortOrder < y.sortOrder) {
      return -1;
    } else if (x.sortOrder > y.sortOrder) {
      return 1;
    }
    return 0;
  });
}

let saveSortOrder = async function () {
  let responseTypeOrderRequest = {
    portalId: property.value.portalId,
    responseTypeOrder: responseSortOrder.value.map((r) => {
      return {
        responseTypeId: r.responseTypeId,
        sortOrder: r.sortOrder,
      } as ResponseTypeOrder;
    }),
  } as ResponseTypeOrderRequest;

  console.log(responseTypeOrderRequest);

  saveError.value = await respondStore.saveResponseOrder(responseTypeOrderRequest);
  if (!saveError.value) {
    await respondStore.getExcludedResponseTypes(property.value.propertyId);
    await bldgStore.tryGetBuildingData(bldgStore.siteCode, true);
    isEditScenarioSortOrderPopupVisible.value = false;
  }
};
// #endregion Edit Scenario Sort Order

// #region Search Response Library

let responseSearchString = ref("");
function searchResponseContent() {
  respondStore.searchResponseContentByPortal(
    responseSearchString.value,
    property.value.portalId
  );
  router.push("/staff/respondsearchresults");
}

// #endregion Search Respond Library

// #region Helpers

var getSiteRoleByName = (roleName: string) =>
  property.value?.siteRoles?.find((r) => r.roleAlias === roleName);

// #endregion Helpers
</script>

<style scoped>
:deep(.ck-editor__editable_inline) {
  min-height: 25vh;
  max-height: max-content;
}

.response-steps,
.ckeditor-text {
  font-size: medium;
}

.response-steps :deep(a),
.ckeditor-text :deep(a) {
  color: blue;
  text-decoration: underline;
  cursor: pointer;
}

.response-steps :deep(a:hover),
.ckeditor-text :deep(a:hover) {
  color: cornflowerblue;
}

.response-steps :deep(ul),
.ckeditor-text :deep(ul) {
  padding-left: 24px;
}

.response-steps :deep(ul > li),
.ckeditor-text :deep(ul > li) {
  font-weight: normal;
  list-style-type: circle;
}

.response-steps :deep(ol),
.ckeditor-text :deep(ol) {
  counter-reset: li;
  margin-left: 0;
  padding-left: 0;
}

.response-steps :deep(ol > li),
.ckeditor-text :deep(ol > li) {
  position: relative;
  margin: 0 0 2px 2em;
  padding: 0 8px;
  list-style: none;
}

.response-steps :deep(ol > li:before),
.ckeditor-text :deep(ol > li:before) {
  content: counter(li);
  counter-increment: li;
  position: absolute;
  top: 0px;
  left: -1em;
  box-sizing: border-box;
}

.response-steps :deep(h2),
.ckeditor-text :deep(h2) {
  font-size: x-large;
}

.response-steps :deep(h3),
.ckeditor-text :deep(h3) {
  font-size: larger;
}

.response-steps :deep(h4),
.ckeditor-text :deep(h4) {
  font-size: large;
}
</style>
